import { memo, type JSX, useState } from "react";
import { useRouter } from "next/router";
import { type Row } from "@tanstack/react-table";
import { urlRegex } from "@langfuse/shared";
import {
  SMALL_ARRAY_THRESHOLD,
  SMALL_OBJECT_THRESHOLD,
  objectFitsInSingleRowPreview,
  type JsonTableRow,
} from "@/src/components/table/utils/jsonExpansionUtils";
import { classifyMediaValue } from "@/src/components/ui/media/mediaUtils";
import { MediaReferenceTag } from "@/src/components/ui/media/MediaReferenceTag";
import { copyTextToClipboard } from "@/src/utils/clipboard";
import { Button } from "@/src/components/ui/button";
import {
  DropdownMenuController,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from "@/src/components/ui/dropdown-menu";
import { cn } from "@/src/utils/tailwind";
import {
  buildEventsTablePathForMetadataFilter,
  type MetadataFilterOperator,
} from "@/src/features/events";
import { Copy, Check, EllipsisVertical, Filter, FilterX } from "lucide-react";

/**
 * Enables the per-row actions menu in a metadata JSON view: copy value/
 * structure/path plus "Include in / Exclude from filter" shortcuts that land on
 * the matching events table. Passed only by the metadata view, so input/output
 * cells keep their plain one-click copy button.
 */
export type MetadataFilterActions = {
  projectId: string;
  filterTarget: "observations" | "traces";
};

const MAX_STRING_LENGTH_FOR_LINK_DETECTION = 1500;
const MAX_CELL_DISPLAY_CHARS = 2000;
const ARRAY_PREVIEW_ITEMS = 3;
const VALUE_TEXT_CLASSES = "font-mono text-xs/5 wrap-break-word";
const PREVIEW_TEXT_CLASSES = "text-gray-500 dark:text-gray-400";

const ROW_ACTION_BUTTON_CLASSES =
  "text-muted-foreground absolute top-0 h-5 w-5 rounded-sm p-0 opacity-0 transition-opacity duration-200 group-hover/value-cell:opacity-100 hover:bg-transparent hover:text-foreground";
const ROW_COPY_OFFSET = "-right-0.5";
const ROW_MENU_OFFSET = "-right-1.5";

function renderStringWithLinks(text: string): React.ReactNode {
  if (text.length >= MAX_STRING_LENGTH_FOR_LINK_DETECTION) {
    return text;
  }

  const localUrlRegex = new RegExp(urlRegex.source, "gi");
  const parts = text.split(localUrlRegex);
  const matches = text.match(localUrlRegex) || [];

  const result: React.ReactNode[] = [];
  let matchIndex = 0;

  for (let i = 0; i < parts.length; i++) {
    if (parts[i]) {
      result.push(parts[i]);
    }

    if (matchIndex < matches.length) {
      const url = matches[matchIndex];
      result.push(
        <a
          key={`link-${matchIndex}`}
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          className="hover:opacity-80"
          onClick={(e) => e.stopPropagation()} // no row expansion when clicking links
        >
          {url}
        </a>,
      );
      matchIndex++;
    }
  }

  return result;
}

function getValueType(value: unknown): JsonTableRow["type"] {
  if (value === null) return "null";
  if (value === undefined) return "undefined";
  if (Array.isArray(value)) return "array";
  return typeof value as JsonTableRow["type"];
}

function arrayPreviewText(arr: unknown[]): string {
  if (arr.length === 0) return "empty list";

  if (arr.length <= SMALL_ARRAY_THRESHOLD) {
    // Show inline values for small arrays
    const displayItems = arr
      .map((item) => {
        const itemType = getValueType(item);
        if (itemType === "string") return JSON.stringify(item);
        if (itemType === "object" && item !== null) {
          const obj = item as Record<string, unknown>;
          const keys = Object.keys(obj);
          if (keys.length === 0) return "{}";
          if (keys.length <= SMALL_OBJECT_THRESHOLD) {
            const keyPreview = keys.map((k) => `"${k}": ...`).join(", ");
            return `{${keyPreview}}`;
          }
          return `{"${keys[0]}": ...}`;
        }
        if (itemType === "array") return "...";
        return String(item);
      })
      .join(", ");
    return `[${displayItems}]`;
  }
  // Show truncated values for large arrays
  const preview = arr
    .slice(0, ARRAY_PREVIEW_ITEMS)
    .map((item) => {
      const itemType = getValueType(item);
      if (itemType === "string") return JSON.stringify(item);
      if (itemType === "object" || itemType === "array") return "...";
      return String(item);
    })
    .join(", ");
  return `[${preview}, ...${arr.length - ARRAY_PREVIEW_ITEMS} more]`;
}

function renderPreview(text: string): JSX.Element {
  return <span className={PREVIEW_TEXT_CLASSES}>{text}</span>;
}

function formatPreviewPrimitive(value: unknown): string {
  if (typeof value === "string") return JSON.stringify(value);
  if (value === null) return "null";
  return String(value);
}

function formatShortObjectPreview(obj: Record<string, unknown>): string | null {
  if (!objectFitsInSingleRowPreview(obj)) return null;
  const fields = Object.entries(obj).map(
    ([key, field]) =>
      `${JSON.stringify(key)}: ${formatPreviewPrimitive(field)}`,
  );
  return `{${fields.join(", ")}}`;
}

function objectPreviewText(obj: Record<string, unknown>): string {
  const keys = Object.keys(obj);
  if (keys.length === 0) return "empty object";
  return formatShortObjectPreview(obj) ?? `${keys.length} items`;
}

function getValueStringLength(value: unknown): number {
  if (typeof value === "string") {
    return value.length;
  }
  try {
    return JSON.stringify(value).length;
  } catch {
    return String(value).length;
  }
}

function getTruncatedValue(value: string, maxChars: number): string {
  if (value.length <= maxChars) {
    return value;
  }

  const truncated = value.substring(0, maxChars);
  const lastSpaceIndex = truncated.lastIndexOf(" ");

  // Try to truncate at word boundary if possible
  if (lastSpaceIndex > maxChars * 0.8) {
    return truncated.substring(0, lastSpaceIndex) + "...";
  }

  return truncated + "...";
}

export function getCopyValue(value: unknown): string {
  if (typeof value === "string") {
    return value; // Return string without quotes
  }
  if (value === null) return "null";
  if (value === undefined) return "undefined";

  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
}

/** Walks up to the top-level (level 0) ancestor key for a metadata row. */
function resolveTopLevelMetadataKey(row: Row<JsonTableRow>): string {
  let cursor: Row<JsonTableRow> | undefined = row;
  while (cursor && cursor.original.level > 0) {
    cursor = cursor.getParentRow() ?? undefined;
  }
  return cursor?.original.key ?? row.original.key;
}

/**
 * Builds the dotted key path from the row's actual keys (root → leaf). Unlike
 * `convertRowIdToKeyPath`, which reconstructs the path from the hyphen-joined
 * row id, this is lossless for keys that themselves contain `-` (e.g.
 * `x-request-id`).
 */
function resolveKeyPath(row: Row<JsonTableRow>): string {
  const keys: string[] = [];
  let cursor: Row<JsonTableRow> | undefined = row;
  while (cursor) {
    keys.unshift(String(cursor.original.key));
    cursor = cursor.getParentRow() ?? undefined;
  }
  return keys.join(".");
}

/**
 * The per-row overflow menu shown in metadata views. Containers offer "Copy
 * structure"; scalar leaves offer "Copy value" plus filter shortcuts. Rendered
 * only when `metadataActions` is provided, so `useRouter` stays off the hot
 * path for the (far more numerous) input/output JSON cells.
 */
function ValueCellActionsMenuContent({
  row,
  metadataActions,
}: {
  row: Row<JsonTableRow>;
  metadataActions: MetadataFilterActions;
}) {
  const router = useRouter();
  const { value, type, hasChildren, level } = row.original;

  const filterValue = String(value);
  // A nested value is matched as a substring of its JSON-ENCODED top-level
  // branch, but `filterValue` is the JSON-parsed (unescaped) form shown in the
  // tree. When the value carries JSON-escapable characters (quotes,
  // backslashes, newlines) the two differ, so `contains` would never match —
  // hide the shortcut rather than build a confidently-wrong filter.
  //
  // Top-level scalars are *usually* stored raw (ingestion keeps top-level
  // strings as-is), so we skip the check there. This heuristic misses the rare
  // case of a top-level value that was itself JSON-encoded with escapes; we
  // accept that miss because the alternative — always applying the check —
  // would wrongly hide the far more common top-level raw string with literal
  // newlines (which filters fine), since its encoded form also differs.
  const valueMatchesStoredForm =
    level === 0 || JSON.stringify(filterValue).slice(1, -1) === filterValue;
  const isScalarLeaf =
    !hasChildren &&
    (type === "string" || type === "number" || type === "boolean") &&
    // Skip empty values: `contains ""` matches every row (ClickHouse
    // position(x, "") === 1, and Map[missingKey] defaults to "") while
    // `does not contain ""` matches none — both shortcuts would be no-ops.
    filterValue.length > 0 &&
    valueMatchesStoredForm;

  // Metadata is a flat Map(String, String), so a nested value can only be
  // matched as a substring of its top-level branch. We use contains/does not
  // contain uniformly (top-level included) so Include and Exclude stay exact
  // complements — stringObject has no "!=" to invert an exact "=".
  const metadataKey = resolveTopLevelMetadataKey(row);
  const includeOperator: MetadataFilterOperator = "contains";
  const excludeOperator: MetadataFilterOperator = "does not contain";
  const displayValue = type === "string" ? `"${filterValue}"` : filterValue;

  const handleCopyData = () => {
    copyTextToClipboard(getCopyValue(value));
  };
  const handleCopyPath = () => {
    copyTextToClipboard(resolveKeyPath(row));
  };
  const navigateWithFilter = (operator: MetadataFilterOperator) => {
    router.push(
      buildEventsTablePathForMetadataFilter({
        currentPath: router.asPath,
        projectId: metadataActions.projectId,
        metadataKey,
        value: filterValue,
        operator,
        target: metadataActions.filterTarget,
      }),
    );
  };

  const includeFilterText = `metadata.${metadataKey} ${includeOperator} ${displayValue}`;
  const excludeFilterText = `metadata.${metadataKey} ${excludeOperator} ${displayValue}`;

  return (
    <>
      <DropdownMenuItem className="text-xs" onSelect={handleCopyData}>
        <Copy className="icon-base text-icon-foreground mr-2 shrink-0" />
        {hasChildren ? "Copy structure" : "Copy value"}
      </DropdownMenuItem>
      <DropdownMenuItem className="text-xs" onSelect={handleCopyPath}>
        <Copy className="icon-base text-icon-foreground mr-2 shrink-0" />
        Copy path
      </DropdownMenuItem>
      {isScalarLeaf && (
        <>
          <DropdownMenuSeparator />
          <DropdownMenuItem
            className="text-xs"
            onSelect={() => navigateWithFilter(includeOperator)}
          >
            <Filter className="icon-base text-icon-foreground mr-2 shrink-0" />
            <span className="flex min-w-0 flex-col">
              <span>Include in filter</span>
              <span
                className="text-muted-foreground truncate font-mono"
                title={includeFilterText}
              >
                {includeFilterText}
              </span>
            </span>
          </DropdownMenuItem>
          <DropdownMenuItem
            className="text-xs"
            onSelect={() => navigateWithFilter(excludeOperator)}
          >
            <FilterX className="icon-base text-icon-foreground mr-2 shrink-0" />
            <span className="flex min-w-0 flex-col">
              <span>Exclude from filter</span>
              <span
                className="text-muted-foreground truncate font-mono"
                title={excludeFilterText}
              >
                {excludeFilterText}
              </span>
            </span>
          </DropdownMenuItem>
        </>
      )}
    </>
  );
}

export const ValueCell = memo(
  ({
    row,
    expandedCells,
    toggleCellExpansion,
    preserveStringWhitespace = false,
    metadataActions,
    rowActions,
  }: {
    row: Row<JsonTableRow>;
    expandedCells: Set<string>;
    toggleCellExpansion: (cellId: string) => void;
    preserveStringWhitespace?: boolean;
    metadataActions?: MetadataFilterActions;
    /** Replaces the built-in menu, for tables with their own row actions. */
    rowActions?: (row: Row<JsonTableRow>) => React.ReactNode;
  }) => {
    const { value, type } = row.original;
    const cellId = `${row.id}-value`;
    const isCellExpanded = expandedCells.has(cellId);
    const [showCopySuccess, setShowCopySuccess] = useState(false);

    const handleCopy = async (e: React.MouseEvent) => {
      e.stopPropagation();
      const copyValue =
        typeof value === "string" ? JSON.stringify(value) : getCopyValue(value);

      try {
        await copyTextToClipboard(copyValue);
        setShowCopySuccess(true);
        setTimeout(() => setShowCopySuccess(false), 1500);
      } catch {
        // Copy failed silently
      }
    };

    const getDisplayValue = () => {
      switch (type) {
        case "string": {
          const stringValue = String(value);

          // Render previewable media (Langfuse refs, data URIs, media URLs) as a
          // hover-to-peek chip instead of the raw string.
          const mediaDescriptor = classifyMediaValue(stringValue);
          if (mediaDescriptor) {
            return {
              content: <MediaReferenceTag descriptor={mediaDescriptor} />,
              needsTruncation: false,
            };
          }

          const needsTruncation = stringValue.length > MAX_CELL_DISPLAY_CHARS;
          const displayValue =
            needsTruncation && !isCellExpanded
              ? getTruncatedValue(stringValue, MAX_CELL_DISPLAY_CHARS)
              : stringValue;

          return {
            content: (
              <span
                className={`text-json-value-string ${
                  preserveStringWhitespace
                    ? "whitespace-pre-wrap"
                    : "whitespace-pre-line"
                }`}
              >
                {renderStringWithLinks(displayValue)}
              </span>
            ),
            needsTruncation,
          };
        }
        case "number":
          return {
            content: (
              <span className="text-json-value-number">{String(value)}</span>
            ),
            needsTruncation: false,
          };
        case "boolean":
          return {
            content: (
              <span className="text-json-value-boolean">{String(value)}</span>
            ),
            needsTruncation: false,
          };
        case "null":
          return {
            content: (
              <span className="text-json-value-nullish italic">null</span>
            ),
            needsTruncation: false,
          };
        case "undefined":
          return {
            content: <span className="text-json-value-nullish">undefined</span>,
            needsTruncation: false,
          };
        case "array": {
          // Hide the parent preview only when expanded children are actually
          // rendered. showNullValues={false} can filter every child out while
          // leaving the expand chevron (hasChildren is computed on the raw
          // array), and blanking the cell then loses the only remaining value.
          const hasVisibleChildRows =
            row.getIsExpanded() && row.subRows.length > 0;
          if (hasVisibleChildRows) {
            return {
              content: null,
              needsTruncation: false,
            };
          }
          const arrayPreview = arrayPreviewText(value as unknown[]);
          return {
            content: renderPreview(arrayPreview),
            needsTruncation: false,
            previewTitle: arrayPreview,
          };
        }
        case "object": {
          const hasVisibleChildRows =
            row.getIsExpanded() && row.subRows.length > 0;
          if (hasVisibleChildRows) {
            return {
              content: null,
              needsTruncation: false,
            };
          }
          const objectPreview = objectPreviewText(
            value as Record<string, unknown>,
          );
          return {
            content: renderPreview(objectPreview),
            needsTruncation: false,
            previewTitle: objectPreview,
          };
        }
        default: {
          const stringValue = String(value);
          const needsTruncation = stringValue.length > MAX_CELL_DISPLAY_CHARS;
          const displayValue =
            needsTruncation && !isCellExpanded
              ? getTruncatedValue(stringValue, MAX_CELL_DISPLAY_CHARS)
              : stringValue;

          return {
            content: (
              <span className="text-gray-600 dark:text-gray-400">
                {displayValue}
              </span>
            ),
            needsTruncation,
          };
        }
      }
    };

    const { content, needsTruncation, previewTitle } = getDisplayValue();
    const singleLine = previewTitle !== undefined;

    return (
      <div
        className={cn(
          VALUE_TEXT_CLASSES,
          "group/value-cell relative max-w-full",
          singleLine && "w-0 min-w-full",
        )}
      >
        <span
          className={cn("cursor-text", singleLine && "block truncate")}
          title={previewTitle}
        >
          {content}
        </span>
        {needsTruncation && !row.original.hasChildren && (
          <div
            className="inline cursor-pointer opacity-50"
            onClick={(e) => {
              e.stopPropagation();
              toggleCellExpansion(cellId);
            }}
          >
            {isCellExpanded
              ? "\n...collapse"
              : `\n...expand (${getValueStringLength(value) - MAX_CELL_DISPLAY_CHARS} more characters)`}
          </div>
        )}

        {/* Hover affordance: a one-click copy by default, or an actions menu
            (copy + filter shortcuts) in metadata views. */}
        {rowActions || metadataActions ? (
          <DropdownMenuController
            align="end"
            maxWidth="320px"
            renderMenu={() => {
              if (rowActions) {
                return rowActions(row);
              }
              if (metadataActions) {
                return (
                  <ValueCellActionsMenuContent
                    row={row}
                    metadataActions={metadataActions}
                  />
                );
              }
              return null;
            }}
          >
            {({ isOpen, Trigger }) => (
              <Trigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  aria-label="Value actions"
                  title="Actions"
                  className={cn(
                    ROW_ACTION_BUTTON_CLASSES,
                    ROW_MENU_OFFSET,
                    isOpen && "opacity-100",
                  )}
                  onClick={(event) => event.stopPropagation()}
                >
                  <EllipsisVertical className="icon-base text-icon-foreground" />
                </Button>
              </Trigger>
            )}
          </DropdownMenuController>
        ) : (
          <Button
            variant="ghost"
            size="icon"
            className={cn(ROW_ACTION_BUTTON_CLASSES, ROW_COPY_OFFSET)}
            onClick={handleCopy}
            title="Copy value"
            aria-label="Copy cell value"
          >
            {showCopySuccess ? (
              <Check className="icon-base text-icon-foreground" />
            ) : (
              <Copy className="icon-base text-icon-foreground" />
            )}
          </Button>
        )}
      </div>
    );
  },
);

ValueCell.displayName = "ValueCell";

// Export utilities that might be needed elsewhere
export { getValueStringLength };
